import React, { useState } from 'react'

import cn from 'classnames'

import ChainToken from '@src/components/ChainToken'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import SelectTokenModal from '@src/pages/FiatExchange/components/SelectTokenModal'
import { supportedTokens } from '@src/pages/FiatExchange/config'
import { INetwork } from '@src/types/network'
import { IToken } from '@src/types/token'

interface TokenSelectionPillProps {
    onChange: (__newData: { chainId?: number; token?: IToken }) => void
    chainId?: number
    token?: IToken
    availableNetworks: Record<string, INetwork>
    disabledChains?: number[]
    showNetwork?: boolean
    tokenIconSize?: number
    chainIconSize?: number
    /** Whether to show the chevron icon next to the token name */
    useChevronDown?: boolean
}

const TokenSelectionPill: React.FC<TokenSelectionPillProps> = ({
    onChange,
    chainId,
    token,
    availableNetworks,
    disabledChains,
    showNetwork,
    tokenIconSize = 40,
    chainIconSize = 16,
    useChevronDown = true,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const handleOpenModal = () => setIsOpen(true)
    const handleCloseModal = () => setIsOpen(false)

    return (
        <div className='relative'>
            {showNetwork ? (
                <div
                    className={cn(
                        'flex h-7 cursor-pointer items-center space-x-2 rounded-md border-mach-button-secondary-border bg-mach-button-secondary-background px-[10px]',
                        isOpen ? 'border-mach-modal-border' : 'border-transparent'
                    )}
                    onClick={handleOpenModal}
                >
                    <img
                        src={availableNetworks[chainId || -100]?.icon}
                        alt=''
                        className='h-[18px] w-[18px] rounded-md'
                        onError={e => {
                            e.currentTarget.src = '/assets/token/unknown.png'
                        }}
                    />
                    <p className='font-normal text-mach-accent-forgeround'>
                        {chainId && availableNetworks[chainId]?.name}
                    </p>
                    <ChevronDownIcon className={cn('h-4 w-4 transition-all', isOpen ? 'rotate-180' : '')} />
                </div>
            ) : (
                <div
                    className={cn(
                        'flex cursor-pointer items-center',
                        isOpen ? 'border-mach-modal-border' : 'border-transparent'
                    )}
                    onClick={handleOpenModal}
                >
                    <div className='flex h-8 w-[50px] items-center md:h-10'>
                        <ChainToken
                            tokenLogo={token?.name === 'BitTorrent' ? '/assets/token/btt.png' : token?.logoURI}
                            networkIcon={token?.chainId ? availableNetworks[token?.chainId].icon : ''}
                            tokenIconSize={tokenIconSize}
                            chainIconSize={chainIconSize}
                        />
                    </div>
                    <p className='max-w-[90px] truncate px-1 text-xl font-medium md:max-w-[110px] md:text-[32px]'>
                        {token?.symbol || 'Select'}
                    </p>
                    {useChevronDown && <ChevronDownIcon />}
                </div>
            )}

            <SelectTokenModal
                isOpen={isOpen}
                onClose={handleCloseModal}
                selectedChain={chainId || ''}
                setSelectedChain={__chainId => {
                    onChange({ chainId: Number(__chainId) })
                }}
                selectedToken={token}
                setSelectedToken={__token => {
                    onChange({ token: __token, chainId: __token.chainId })
                }}
                availableNetworks={availableNetworks}
                disabledChains={disabledChains}
                allowedTokens={supportedTokens}
            />
        </div>
    )
}

export default TokenSelectionPill
